import React from 'react';
import { View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import { useTemplateColors } from 'PDF/TemplateEngine/TemplateColorsContext';
import SectionHeader from 'PDF/TemplateEngine/atoms/SectionHeader';
import StatusTag from 'PDF/TemplateEngine/atoms/StatusTag';
import buildEquiposFromInspeccion from 'PDF/documents/informeMantencion/buildEquiposFromInspeccion';

// Body type 'inspection-per-equipment': layout vertical que escala con N
// ascensores. Reemplaza la matriz pivotada (columnas = ascensores) por:
//   1. una tabla resumen "semáforo" (una fila por ascensor), y
//   2. un bloque por ascensor con el detalle completo de mantenimiento.
// Tono mesurado: solo escala a rojo si hay un hallazgo ALTA/CRITICA.

const ESTADO_META = {
  conforme: { label: 'Conforme', tone: 'ok' },
  observaciones: { label: 'Con observaciones', tone: 'signal' },
  critico: { label: 'Requiere atención inmediata', tone: 'err' },
};

const GRAVE = new Set(['ALTA', 'CRITICA']);

function InspectionPerEquipmentSection({ section, data }) {
  const { colors, FONT } = useTemplateColors();
  const cfg = section?.config || {};
  const inspeccion = Array.isArray(data?.[cfg.sourceKey]) ? data[cfg.sourceKey] : [];
  const equipos = buildEquiposFromInspeccion(inspeccion);

  const s = StyleSheet.create({
    // ── resumen semáforo ──
    summary: {
      border: `1px solid ${colors.border}`,
      borderRadius: 4,
      overflow: 'hidden',
      marginBottom: '0.35cm',
    },
    thead: {
      flexDirection: 'row',
      backgroundColor: colors.lightBg,
      borderBottom: `1px solid ${colors.border}`,
    },
    th: {
      padding: '0.15cm 0.2cm',
      fontSize: 7.5,
      fontFamily: FONT.bold,
      color: colors.textFade || colors.muted,
      textTransform: 'uppercase',
    },
    tr: { flexDirection: 'row', borderBottom: `1px solid ${colors.border}`, alignItems: 'center' },
    td: {
      padding: '0.15cm 0.2cm',
      fontSize: 8.5,
      fontFamily: FONT.base,
      color: colors.text || colors.darkText,
    },
    colAsc: { width: '34%' },
    colNum: { width: '16%', textAlign: 'center' },
    colEstado: { width: '34%' },

    // ── bloque por ascensor ──
    block: { marginBottom: '0.4cm' },
    blockHead: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: '0.25cm',
      backgroundColor: colors.lightBg,
      borderRadius: 4,
      padding: '0.2cm 0.25cm',
      marginBottom: '0.2cm',
    },
    blockTitle: { fontSize: 11, fontFamily: FONT.bold, color: colors.ink || colors.darkText },
    blockMeta: { fontSize: 8, fontFamily: FONT.base, color: colors.muted, marginLeft: 'auto' },

    sector: { marginTop: '0.25cm' },
    // contenedor redondeado del sector (misma estética que la tabla semáforo)
    sectorBox: { border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' },
    arow: { padding: '0.22cm 0.28cm' },
    arowDivider: { borderTop: `1px solid ${colors.border}` },
    ahead: { flexDirection: 'row', alignItems: 'flex-start', gap: '0.22cm' },
    aname: {
      flex: 1,
      fontSize: 9.5,
      fontFamily: FONT.base,
      color: colors.text || colors.darkText,
      lineHeight: 1.4,
      paddingTop: '0.02cm',
    },
    anameFalla: { fontFamily: FONT.bold },
    // bloque de hallazgo: barra de acento lateral (look sobrio de informe)
    hacc: { marginTop: '0.16cm', paddingLeft: '0.28cm', borderLeft: `2px solid #e7c6c1` },
    sacc: { marginTop: '0.16cm', paddingLeft: '0.28cm', borderLeft: `2px solid ${colors.border}` },
    hLabel: {
      fontSize: 7,
      fontFamily: FONT.bold,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
      color: colors.textFade || colors.muted,
      marginBottom: '0.08cm',
    },
    hitemDivider: {
      borderTop: `1px solid ${colors.border}`,
      marginTop: '0.14cm',
      paddingTop: '0.14cm',
    },
    hd: {
      fontSize: 9,
      fontFamily: FONT.base,
      color: colors.text || colors.darkText,
      lineHeight: 1.45,
    },
    sev: {
      fontSize: 7,
      fontFamily: FONT.bold,
      color: '#9b2a1e',
      textTransform: 'uppercase',
      letterSpacing: 0.3,
      marginTop: '0.05cm',
    },
    // Fila de miniaturas de fotos del hallazgo (cuadradas, recortadas a llenar).
    thumbs: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: '0.18cm',
      marginTop: '0.16cm',
    },
    thumb: {
      width: '2.4cm',
      height: '2.4cm',
      objectFit: 'cover',
      borderRadius: 3,
      border: `1px solid ${colors.border}`,
    },
  });

  if (equipos.length === 0) return null;

  // Miniaturas de las fotos de un hallazgo (máx. 4, cuadradas).
  const Thumbs = ({ imagenes }) =>
    imagenes && imagenes.length ? (
      <View style={s.thumbs}>
        {imagenes.slice(0, 4).map((img, ki) => (
          <Image key={ki} src={img.url} style={s.thumb} />
        ))}
      </View>
    ) : null;

  return (
    <View>
      {/* 1 ── Resumen semáforo por ascensor */}
      <View style={s.summary}>
        <View style={s.thead}>
          <Text style={[s.th, s.colAsc]}>Ascensor</Text>
          <Text style={[s.th, s.colNum]}>Conformes</Text>
          <Text style={[s.th, s.colNum]}>Fallas</Text>
          <Text style={[s.th, s.colEstado]}>Estado</Text>
        </View>
        {equipos.map((eq) => {
          const meta = ESTADO_META[eq.estado_global] || ESTADO_META.observaciones;
          return (
            <View key={eq.key} style={s.tr} wrap={false}>
              <Text style={[s.td, s.colAsc]}>{eq.nombre}</Text>
              <Text style={[s.td, s.colNum]}>{eq.conteos.conformes}</Text>
              <Text style={[s.td, s.colNum]}>{eq.conteos.fallas}</Text>
              <View style={[s.td, s.colEstado]}>
                <StatusTag tone={meta.tone}>{meta.label}</StatusTag>
              </View>
            </View>
          );
        })}
      </View>

      {/* 2 ── Un bloque por ascensor */}
      {equipos.map((eq) => {
        const meta = ESTADO_META[eq.estado_global] || ESTADO_META.observaciones;
        return (
          <View key={eq.key} style={s.block}>
            <View style={s.blockHead} wrap={false}>
              <Text style={s.blockTitle}>{eq.nombre}</Text>
              <StatusTag tone={meta.tone}>{meta.label}</StatusTag>
              <Text style={s.blockMeta}>
                {eq.conteos.conformes} de {eq.conteos.actividades} conformes
              </Text>
            </View>

            {eq.sectores.map((sec, si) => (
              <View key={si} style={s.sector}>
                <SectionHeader
                  title={sec.sector}
                  count={`${sec.items.length} ${
                    sec.items.length === 1 ? 'actividad' : 'actividades'
                  }`}
                />
                <View style={s.sectorBox}>
                  {sec.items.map((item, ii) => {
                    const falla = item.estado === 'falla';
                    const incidencias = item.incidencias || [];
                    const sugerencias = item.sugerencias || [];
                    return (
                      <View key={ii} style={[s.arow, ii > 0 ? s.arowDivider : null]} wrap={false}>
                        <View style={s.ahead}>
                          <StatusTag tone={falla ? 'err' : 'ok'}>
                            {falla ? 'Falla' : 'Conforme'}
                          </StatusTag>
                          <Text style={[s.aname, falla ? s.anameFalla : null]}>{item.act}</Text>
                        </View>

                        {/* Incidencias (con severidad) */}
                        {incidencias.length > 0 ? (
                          <View style={s.hacc}>
                            <Text style={s.hLabel}>
                              {incidencias.length === 1 ? 'Incidencia' : 'Incidencias'}
                            </Text>
                            {incidencias.map((h, hi) => (
                              <View key={hi} style={hi > 0 ? s.hitemDivider : null}>
                                <Text style={s.hd}>{h.descripcion}</Text>
                                {GRAVE.has(h.severidad) ? (
                                  <Text style={s.sev}>Severidad {h.severidad.toLowerCase()}</Text>
                                ) : null}
                                <Thumbs imagenes={h.imagenes} />
                              </View>
                            ))}
                          </View>
                        ) : null}

                        {/* Sugerencias (etiquetadas) */}
                        {sugerencias.length > 0 ? (
                          <View style={s.sacc}>
                            <Text style={s.hLabel}>
                              {sugerencias.length === 1 ? 'Sugerencia' : 'Sugerencias'}
                            </Text>
                            {sugerencias.map((h, hi) => (
                              <View key={hi} style={hi > 0 ? s.hitemDivider : null}>
                                <Text style={s.hd}>{h.descripcion}</Text>
                                <Thumbs imagenes={h.imagenes} />
                              </View>
                            ))}
                          </View>
                        ) : null}
                      </View>
                    );
                  })}
                </View>
              </View>
            ))}
          </View>
        );
      })}
    </View>
  );
}

export default InspectionPerEquipmentSection;
